import { classNames } from "@/lib/utils";
import { EvaluationRun, EvaluationStatsResponse, EvaluationEvaluatorStats } from "@/services/evaluationReports.service";
import { Evaluator } from "@/types/app/evaluator";
import { isNil } from "lodash";
import { findStatsForRunAndEvaluator } from "@/lib/evaluations";
import { Delta } from "../Delta";
import { renderAggregateValue } from "../../../EvaluationResult";
import { useEvaluationContext } from "@/context/EvaluationContext";
import React, { useMemo } from "react";
import { analytics } from "@/lib/analytics/analytics.client";
import { useFilterContext } from "../../EvaluationFilterContext";
import { Filter } from "@/lib/filtering/filters";
/** Renders columns for the specified stat for the given Runs. */
export const StatRow = <T extends EvaluationEvaluatorStats>({
  runs,
  referenceRun,
  evaluator,
  evaluationStats,
  calculateValue,
  renderValue = "normal",
  className,
  higherIsBetter,
  getValueFilters,
  getDeltaFilters,
}: {
  runs: EvaluationRun[];
  referenceRun?: EvaluationRun;
  evaluator: Evaluator;
  evaluationStats: EvaluationStatsResponse;
  // Callback to calculate the value to display for the specified stat.
  calculateValue: (stats: T | undefined, run: EvaluationRun) => number;
  // How to render the calculated value. If "aggregate", uses `renderAggregateValue` to render the value (that renders the value based on the Evaluator return type)
  renderValue?:
    | "normal"
    | "aggregate"
    | ((evaluator: Evaluator, value: number, stats: T | undefined) => { value: number; displayValue: React.ReactNode });
  higherIsBetter?: boolean;
  className?: string;
  // Callback to get the filters for a clickthrough on the value, if any
  getValueFilters?: (stats: T | undefined, referenceStats: T | undefined) => Filter[] | undefined;
  // Callback to get the filters for a clickthrough on the delta, if any
  getDeltaFilters?: (stats: T | undefined, referenceStats: T | undefined) => Filter[] | undefined;
}) => {
  const { setTab } = useEvaluationContext();
  const { setFilters } = useFilterContext();

  const evaluatorVersionId = evaluator.version_id;
  const referenceStats = referenceRun
    ? findStatsForRunAndEvaluator<T>(referenceRun, evaluatorVersionId, evaluationStats.run_stats)
    : undefined;

  // TODO these precomputed properties could just depend on runIds instead of the entire runs array,
  // which might decrease number of recomputes
  const evaluatorStatsByRun = useMemo(
    () =>
      runs.reduce(
        (acc, run) => {
          const stats = findStatsForRunAndEvaluator<T>(run, evaluatorVersionId, evaluationStats.run_stats);
          acc[run.id] = stats;
          return acc;
        },
        {} as Record<string, T | undefined>,
      ),
    [runs, evaluatorVersionId, evaluationStats.run_stats],
  );

  // Precompute filters for each run so we can use them in conditionals and avoid rerendering
  const valueFiltersByRun = useMemo(
    () =>
      runs.reduce(
        (acc, run) => {
          const filters = getValueFilters?.(evaluatorStatsByRun[run.id], referenceStats);
          acc[run.id] = filters;
          return acc;
        },
        {} as Record<string, Filter[] | undefined>,
      ),
    [runs, evaluatorStatsByRun, referenceStats, getValueFilters],
  );
  const deltaFiltersByRun = useMemo(
    () =>
      runs.reduce(
        (acc, run) => {
          const filters = getDeltaFilters?.(evaluatorStatsByRun[run.id], referenceStats);
          acc[run.id] = filters;
          return acc;
        },
        {} as Record<string, Filter[] | undefined>,
      ),
    [runs, evaluatorStatsByRun, referenceStats, getDeltaFilters],
  );

  return (
    <>
      {runs.map((run) => {
        const evaluatorStats = evaluatorStatsByRun[run.id];
        const calculatedValue = calculateValue(evaluatorStats, run);
        let renderedValue: { value: number; displayValue: React.ReactNode };
        switch (renderValue) {
          case "normal":
            renderedValue = {
              value: calculatedValue,
              displayValue: calculatedValue,
            };
            break;
          case "aggregate":
            renderedValue = renderAggregateValue(evaluator, calculatedValue);
            break;
          default:
            renderedValue = renderValue(evaluator, calculatedValue, evaluatorStats);
        }

        const valueFilters = calculatedValue !== 0 ? valueFiltersByRun[run.id] : undefined;
        const deltaFilters = deltaFiltersByRun[run.id];

        return (
          <StatValue
            key={run.id}
            value={renderedValue.value}
            displayValue={renderedValue.displayValue}
            comparison={
              referenceRun
                ? run === referenceRun
                  ? null
                  : {
                      value: calculateValue(referenceStats, referenceRun),
                      higherIsBetter,
                    }
                : undefined
            }
            className={classNames("border-l border-l-stroke-base-3", "border-b border-stroke-base-2", className)}
            valueOnClick={
              valueFilters
                ? () => {
                    setTab("review");
                    setFilters(valueFilters);
                    analytics.track("Button Clicked", {
                      purpose: "filter-value",
                      location: "evaluation-stats",
                      stat: evaluator.version_id,
                      runId: run.id,
                    });
                  }
                : undefined
            }
            deltaOnClick={
              deltaFilters
                ? () => {
                    setTab("review");
                    setFilters(deltaFilters);
                    analytics.track("Button Clicked", {
                      purpose: "filter-delta",
                      location: "evaluation-stats",
                      stat: evaluator.version_id,
                      runId: run.id,
                    });
                  }
                : undefined
            }
          />
        );
      })}
    </>
  );
};

export const StatHeader = ({
  children,
  borderB,
}: React.PropsWithChildren<{
  borderB?: boolean;
}>) => {
  return (
    <div
      className={classNames(
        "ml-20 flex min-h-40 items-center gap-4 pl-8 text-12-16 font-medium text-text-base-1",
        borderB && "border-b border-b-stroke-base-2",
      )}
    >
      {children}
    </div>
  );
};

export const StatValue = ({
  value,
  displayValue,
  comparison,
  className,
  valueOnClick,
  deltaOnClick,
}: {
  value: number;
  displayValue?: React.ReactNode;
  comparison?:
    | {
        value: number;
        higherIsBetter?: boolean;
      }
    | null
    | undefined;
  className?: string;
  valueOnClick?: () => void;
  deltaOnClick?: () => void;
}) => {
  const Value = ({
    displayValue,
    value,
    valueOnClick,
  }: {
    displayValue: React.ReactNode;
    value: number;
    valueOnClick?: () => void;
  }) => {
    return (
      <div
        className={classNames(
          "flex h-20 items-center gap-4 truncate rounded border-none px-6 py-4 text-12-12 font-normal focus:ring-0",
          isNil(valueOnClick) ? "" : "cursor-pointer hover:border hover:bg-background-base-325",
        )}
        onClick={valueOnClick}
      >
        {displayValue ?? value}
      </div>
    );
  };

  return (
    <div className={classNames("flex min-h-40 items-center px-12 text-13-20 tabular-nums text-text-base-2", className)}>
      {isNil(comparison) || value === comparison.value ? (
        <div className={classNames("flex w-full justify-end truncate")}>
          <Value displayValue={displayValue} value={value} valueOnClick={valueOnClick} />
        </div>
      ) : (
        <div className={classNames("flex w-full items-center justify-between gap-4")}>
          <Delta
            value={value}
            comparison={comparison.value}
            higherIsBetter={comparison.higherIsBetter}
            onClick={deltaOnClick}
          />
          <div className={classNames("flex truncate")}>
            <Value displayValue={displayValue} value={value} valueOnClick={valueOnClick} />
          </div>
        </div>
      )}
    </div>
  );
};
